// Scroll-driven vision scene: one continuous graph that progressively reveals
// itself across eight scenes as the camera zooms out from a single permit
// process to a planet-scale network of institutions.
//
// Rendered with Canvas 2D + aggressive batching. Earlier SVG-based versions
// fell over on fast scroll because every frame did setAttribute writes on
// hundreds of <line>/<circle> elements, each triggering CSS-transition
// computation. Canvas redraws the whole scene from scratch each frame at a
// fraction of the cost, with no compositor-level state per element.

const WORLD = 2400;
const C = WORLD / 2;

const SCENES = [
  { title: "A single internal process",
    body: "Opening a food facility. The applicant files, zoning clears, the city issues a permit, fire signs off, occupancy is granted.",
    // Cam centered slightly below the linear column's midpoint so the full
    // 5-node sequence sits above the description card at the bottom of the
    // viewport instead of being clipped behind it.
    cam: { cx: C, cy: C + 10, w: 400 } },
  { title: "The process is actually a network",
    body: "Most processes aren't lines, they're networks. Dozens of roles tangled across departments, agencies, and disciplines.",
    cam: { cx: C, cy: C, w: 700 } },
  { title: "Planapus automates the network",
    body: "Planapus replaces the human routing layer. Edges light up. Manual handoffs collapse into milliseconds.",
    cam: { cx: C, cy: C, w: 720 } },
  { title: "External triggers",
    body: "Every network has entry points. A submitted application, a signed permit, a notarized form arriving on the boundary of the org.",
    cam: { cx: C, cy: C, w: 920 } },
  { title: "The applicant has their own network",
    body: "Behind that single submission is another network. Filings with a regulator, sign-offs from a lender, partners aligning, all just to reach the trigger.",
    cam: { cx: C - 220, cy: C + 30, w: 1300 } },
  { title: "Networks trigger networks",
    body: "Each network, once activated, fires triggers into other agencies whose networks light up in turn.",
    cam: { cx: C, cy: C, w: 1950 } },
  // Synthetic in-between step so the scroll arrives at the planet view
  // gradually instead of jumping +49% on the last scene boundary.
  { title: "A planet of networks",
    body: "Public agencies, private institutions, partners, and counterparties. Every one of them runs its own network. Most of them feed each other.",
    cam: { cx: C, cy: C, w: 2150 } },
  { title: "The Planapus Network",
    body: "Planapus is the connective tissue between every organization's internal network. Hundreds of processes, all flowing. Drag to look around.",
    cam: { cx: C, cy: C, w: 2400 } },
];

const LAYER_COUNT = 7;

// ---------- Seeded PRNG (so the graph layout is deterministic) ----------

let _seed = 1357924680;
function srand() {
  _seed = (_seed * 1103515245 + 12345) & 0x7fffffff;
  return _seed / 0x7fffffff;
}

// ---------- Graph generation ----------

function buildGraph() {
  _seed = 1357924680;
  const nodes = [];
  const edges = [];
  const clusters = [];

  const addNode = (x, y, type, layer, extras = {}) => {
    const n = { id: nodes.length, x, y, type, layer, ...extras };
    nodes.push(n);
    return n;
  };
  const addEdge = (a, b, layer, opts = {}) => {
    edges.push({
      id: edges.length,
      from: a.id, to: b.id, layer,
      automated: !!opts.automated,
      pulse: !!opts.pulse,
      canAutomate: !!opts.canAutomate,
    });
  };
  const dist = (a, b) => Math.hypot(a.x - b.x, a.y - b.y);

  // ---- Layer 0: the linear process (5 nodes in a vertical column) ----
  // Labels follow the pitch deck's centerpiece example (FGF Brands opening a
  // food facility): permits, agency reviews, dependencies. Each node is a
  // role taking the work and passing it on, not a status the work sits in.
  const linearLabels = ["File application", "Zoning review", "Permit issued", "Fire inspection", "Cert of occupancy"];
  const linearRoles = ["applicant", "zoning", "permitting", "fire-marshal", "buildings-dept"];
  const linear = [];
  for (let i = 0; i < 5; i++) {
    const n = addNode(C, C - 110 + i * 50, "human", 0, {
      label: linearLabels[i],
      role: linearRoles[i],
      coreLinear: true,
    });
    linear.push(n);
    if (i > 0) addEdge(linear[i - 1], linear[i], 0, { canAutomate: true });
  }

  // ---- Layer 1: main org web around the linear core ----
  const mainOrgNodes = [...linear];
  const mainOrg = { x: C, y: C, r: 230 };
  clusters.push({ x: mainOrg.x, y: mainOrg.y, r: mainOrg.r + 22, label: "Your organization", layer: 1 });
  for (let i = 0; i < 30; i++) {
    const angle = srand() * Math.PI * 2;
    const r = 70 + srand() * (mainOrg.r - 70);
    const x = mainOrg.x + Math.cos(angle) * r;
    const y = mainOrg.y + Math.sin(angle) * r;
    const isBoundary = r > mainOrg.r * 0.78;
    const type = isBoundary && srand() < 0.6 ? "trigger" : "human";
    const n = addNode(x, y, type, 1, { org: "main", isBoundary });
    mainOrgNodes.push(n);
  }
  for (let i = 5; i < mainOrgNodes.length; i++) {
    const n = mainOrgNodes[i];
    const conn = 2 + Math.floor(srand() * 3);
    const sorted = mainOrgNodes
      .filter(m => m.id !== n.id)
      .map(m => ({ m, d: dist(m, n) }))
      .sort((a, b) => a.d - b.d);
    for (let j = 0; j < conn && j < sorted.length; j++) {
      addEdge(n, sorted[j].m, 1, { canAutomate: srand() < 0.85 });
    }
  }

  // ---- Layer 4: applicant's own preparation network ----
  const extWebCenter = { x: C - 720, y: C + 120 };
  clusters.push({ x: extWebCenter.x, y: extWebCenter.y, r: 200, label: "Applicant", layer: 4 });
  const extWebNodes = [];
  for (let i = 0; i < 16; i++) {
    const angle = srand() * Math.PI * 2;
    const r = 30 + srand() * 160;
    const x = extWebCenter.x + Math.cos(angle) * r;
    const y = extWebCenter.y + Math.sin(angle) * r;
    const isUser = i === 0;
    const type = isUser ? "trigger" : "human";
    const n = addNode(x, y, type, 4, { org: "external-user", isUser });
    extWebNodes.push(n);
  }
  for (let i = 1; i < extWebNodes.length; i++) {
    const conn = 1 + Math.floor(srand() * 2);
    const sorted = extWebNodes
      .filter(m => m.id !== extWebNodes[i].id)
      .map(m => ({ m, d: dist(m, extWebNodes[i]) }))
      .sort((a, b) => a.d - b.d);
    for (let j = 0; j < conn && j < sorted.length; j++) {
      addEdge(extWebNodes[i], sorted[j].m, 4);
    }
  }
  const triggers = mainOrgNodes.filter(n => n.type === "trigger");
  triggers.sort((a, b) => dist(a, extWebCenter) - dist(b, extWebCenter));
  if (triggers[0]) {
    addEdge(extWebNodes[0], triggers[0], 4, { automated: true, pulse: true });
  }

  // ---- Layer 5: four more agencies chained to the main org ----
  // Mirrors the pitch deck's FGF Brands example (FDA, USDA, PA DEP, etc.).
  const orgClusters = [
    { x: C + 480, y: C - 150, r: 150, label: "Federal agency (USDA / FDA)" },
    { x: C + 400, y: C + 360, r: 165, label: "State environmental dept" },
    { x: C - 80,  y: C - 540, r: 140, label: "Municipal authority" },
    { x: C - 480, y: C - 340, r: 150, label: "Independent inspection body" },
  ];
  orgClusters.forEach((cluster, idx) => {
    clusters.push({ x: cluster.x, y: cluster.y, r: cluster.r + 22, label: cluster.label, layer: 5 });
    const count = 16 + Math.floor(srand() * 8);
    const clusterNodes = [];
    for (let i = 0; i < count; i++) {
      const angle = srand() * Math.PI * 2;
      const r = 28 + srand() * (cluster.r - 28);
      const x = cluster.x + Math.cos(angle) * r;
      const y = cluster.y + Math.sin(angle) * r;
      const type = i === 0 ? "trigger" : "human";
      const n = addNode(x, y, type, 5, { org: "org" + idx });
      clusterNodes.push(n);
    }
    for (let i = 1; i < clusterNodes.length; i++) {
      const conn = 1 + Math.floor(srand() * 3);
      const sorted = clusterNodes
        .filter(m => m.id !== clusterNodes[i].id)
        .map(m => ({ m, d: dist(m, clusterNodes[i]) }))
        .sort((a, b) => a.d - b.d);
      for (let j = 0; j < conn && j < sorted.length; j++) {
        addEdge(clusterNodes[i], sorted[j].m, 5);
      }
    }
    const mainTriggers = mainOrgNodes.filter(n => n.type === "trigger");
    mainTriggers.sort((a, b) => dist(a, cluster) - dist(b, cluster));
    if (mainTriggers[0]) {
      addEdge(mainTriggers[0], clusterNodes[0], 5, { automated: true, pulse: true });
    }
  });

  // ---- Layer 6: far vision, small distant clusters ----
  const farLabels = [
    "Tax authority", "Insurance carrier", "Sewer authority", "Audit firm",
    "Compliance bureau", "Healthcare credentialing", "Surety / bond issuer",
    "Zoning board", "Lender / underwriter",
  ];
  let farLabelIdx = 0;
  for (let i = 0; i < 22; i++) {
    const angle = srand() * Math.PI * 2;
    const d = 900 + srand() * 320;
    const cx = C + Math.cos(angle) * d;
    const cy = C + Math.sin(angle) * d;
    if (cx < 60 || cx > WORLD - 60 || cy < 60 || cy > WORLD - 60) continue;
    const r = 55 + srand() * 65;
    const wantLabel = farLabelIdx < farLabels.length && srand() < 0.5;
    if (wantLabel) {
      clusters.push({ x: cx, y: cy, r: r + 18, label: farLabels[farLabelIdx++], layer: 6, small: true });
    } else {
      clusters.push({ x: cx, y: cy, r: r + 18, layer: 6, small: true });
    }
    const count = 6 + Math.floor(srand() * 6);
    const clusterNodes = [];
    for (let j = 0; j < count; j++) {
      const a = srand() * Math.PI * 2;
      const rr = 12 + srand() * (r - 12);
      const x = cx + Math.cos(a) * rr;
      const y = cy + Math.sin(a) * rr;
      const n = addNode(x, y, "human", 6, { org: "far" + i });
      clusterNodes.push(n);
    }
    for (let j = 0; j < clusterNodes.length; j++) {
      const next = clusterNodes[(j + 1) % clusterNodes.length];
      addEdge(clusterNodes[j], next, 6);
    }
    let nearest = nodes[0], bestD = Infinity;
    for (const n of nodes) {
      if (n.layer === 6) continue;
      const dd = (n.x - cx) ** 2 + (n.y - cy) ** 2;
      if (dd < bestD) { bestD = dd; nearest = n; }
    }
    if (nearest && clusterNodes[0]) {
      addEdge(nearest, clusterNodes[0], 6, { automated: true });
    }
  }

  return { nodes, edges, clusters };
}

// ---------- Easing ----------

function smoothstep(a, b, t) {
  const x = Math.max(0, Math.min(1, (t - a) / Math.max(1e-6, (b - a))));
  return x * x * (3 - 2 * x);
}
function lerp(a, b, t) { return a + (b - a) * t; }

// ---------- Per-layer reveal ----------
// Max t is 7 (idx 0..7 across 8 scenes). Layer 6 fades in across a long
// window so the planet view fills in gradually instead of in the last frame.

function layerAlphas(t) {
  const l1 = smoothstep(0.5, 1.5, t);
  const l4 = smoothstep(3.5, 4.5, t);
  const l5 = smoothstep(4.5, 5.5, t);
  const l6 = smoothstep(4.6, 6.7, t);
  return [1, l1, l1, l1, l4, l5, l6];
}
function clusterAlphas(t) {
  return [
    1,
    smoothstep(0.7, 1.7, t),
    1, 1,
    smoothstep(3.7, 4.7, t),
    smoothstep(4.7, 5.7, t),
    smoothstep(4.7, 6.85, t),
  ];
}
function automatedShare(t) { return smoothstep(1.55, 2.15, t); }

// ---------- Drag-pan state (final scene only) ----------

const FINAL_DRAG_THRESHOLD = 6.7;
const DRAG_CLAMP = 1400;
const dragState = {
  enabled: false,
  active: false,
  offsetX: 0,
  offsetY: 0,
  pointerId: null,
  startX: 0,
  startY: 0,
  startOffX: 0,
  startOffY: 0,
  lastViewW: 1,
};

// ---------- Canvas + sprites ----------

let canvas = null;
let ctx = null;
let dpr = 1;
let canvasW = 0;
let canvasH = 0;
let cachedGraph = null;
let sprites = null;
let initialized = false;
let runRaf = false;
let pendingTextSwap = null;

// Cached DOM refs — avoid querySelector per frame.
let visSection = null;
let titleEl = null;
let bodyEl = null;
let cardEl = null;
let finalEl = null;
let progressFillEl = null;

// Pre-classified buckets (built once, reused every frame).
let edgeBuckets = null;     // {pulse, automated, canAutomate, base} → Array(LAYER_COUNT) of edge[]
let nodeBuckets = null;     // {coreLinear, midHumans, farHumans, midTriggers, farTriggers}
let clusterByLayer = null;

function makeHaloSprite(haloR, inner, outer) {
  const pad = 2;
  const size = (haloR + pad) * 2;
  const c = document.createElement("canvas");
  c.width = c.height = size;
  const g = c.getContext("2d");
  const grad = g.createRadialGradient(size / 2, size / 2, 0, size / 2, size / 2, haloR);
  grad.addColorStop(0, inner);
  grad.addColorStop(1, outer);
  g.fillStyle = grad;
  g.beginPath();
  g.arc(size / 2, size / 2, haloR, 0, Math.PI * 2);
  g.fill();
  return { canvas: c, r: haloR + pad };
}

function buildSprites() {
  const amberIn = "rgba(245,160,86,0.7)",  amberOut = "rgba(245,160,86,0)";
  const tealIn  = "rgba(109,214,255,0.7)", tealOut  = "rgba(109,214,255,0)";
  const magIn   = "rgba(255,93,143,0.85)", magOut   = "rgba(255,93,143,0)";
  return {
    core:   { amber: makeHaloSprite(16, amberIn, amberOut),
              teal:  makeHaloSprite(16, tealIn,  tealOut),
              magenta: makeHaloSprite(16, magIn, magOut) },
    normal: { amber: makeHaloSprite(10, amberIn, amberOut),
              teal:  makeHaloSprite(10, tealIn,  tealOut),
              magenta: makeHaloSprite(10, magIn, magOut) },
    far:    { amber: makeHaloSprite(6,  amberIn, amberOut),
              teal:  makeHaloSprite(6,  tealIn,  tealOut),
              magenta: makeHaloSprite(6,  magIn, magOut) },
  };
}

function buildBuckets(graph) {
  const mkLayerArr = () => Array.from({ length: LAYER_COUNT }, () => []);
  const eb = {
    pulse: mkLayerArr(),
    automated: mkLayerArr(),
    canAutomate: mkLayerArr(),
    base: mkLayerArr(),
  };
  for (const e of graph.edges) {
    if (e.pulse) eb.pulse[e.layer].push(e);
    else if (e.automated) eb.automated[e.layer].push(e);
    else if (e.canAutomate) eb.canAutomate[e.layer].push(e);
    else eb.base[e.layer].push(e);
  }
  const nb = {
    coreLinear: [],
    farHumans: mkLayerArr(),
    midHumans: mkLayerArr(),
    midTriggers: mkLayerArr(),
    farTriggers: mkLayerArr(),
  };
  for (const n of graph.nodes) {
    if (n.coreLinear) { nb.coreLinear.push(n); continue; }
    const isFar = n.layer === 6;
    if (n.type === "trigger") {
      (isFar ? nb.farTriggers : nb.midTriggers)[n.layer].push(n);
    } else {
      (isFar ? nb.farHumans : nb.midHumans)[n.layer].push(n);
    }
  }
  const cbl = mkLayerArr();
  for (const c of graph.clusters) cbl[c.layer].push(c);
  return { edgeBuckets: eb, nodeBuckets: nb, clusterByLayer: cbl };
}

function ensureCanvasSize() {
  if (!canvas) return;
  // Cap DPR at 2 — anything higher (some 4K external displays at 3x) burns
  // fill rate for invisible gains since none of our line widths are below 0.4
  // world units anyway.
  dpr = Math.min(2, Math.max(1, window.devicePixelRatio || 1));
  const r = canvas.getBoundingClientRect();
  const w = Math.max(1, Math.round(r.width));
  const h = Math.max(1, Math.round(r.height));
  canvasW = w;
  canvasH = h;
  const pixelW = w * dpr;
  const pixelH = h * dpr;
  if (canvas.width !== pixelW || canvas.height !== pixelH) {
    canvas.width = pixelW;
    canvas.height = pixelH;
  }
}

function onResize() {
  ensureCanvasSize();
  startLoop();
}

// ---------- Edge batching ----------
// One stroke() call per (style, layer) batch instead of per-edge.

function strokeEdgeBatch(edgesArr, alpha) {
  if (!edgesArr || edgesArr.length === 0 || alpha <= 0.001) return;
  ctx.globalAlpha = alpha;
  ctx.beginPath();
  const nodes = cachedGraph.nodes;
  for (let i = 0; i < edgesArr.length; i++) {
    const e = edgesArr[i];
    const a = nodes[e.from], b = nodes[e.to];
    ctx.moveTo(a.x, a.y);
    ctx.lineTo(b.x, b.y);
  }
  ctx.stroke();
}

// ---------- Draw ----------

function draw(now) {
  if (!visSection || !visSection.classList.contains("active")) return;
  if (!canvas || !ctx || !cachedGraph) return;
  if (canvasW <= 0 || canvasH <= 0) return;

  // Scroll → t (0..7 across 8 scenes)
  const rect = visSection.getBoundingClientRect();
  const total = Math.max(1, rect.height - window.innerHeight);
  const scrolled = Math.max(0, Math.min(total, -rect.top));
  const progress = scrolled / total;
  const t = progress * (SCENES.length - 1);
  const idx = Math.min(SCENES.length - 2, Math.floor(t));
  const frac = t - idx;
  const ease = frac * frac * (3 - 2 * frac);
  const cur = SCENES[idx];
  const nxt = SCENES[idx + 1];
  const camCx = lerp(cur.cam.cx, nxt.cam.cx, ease);
  const camCy = lerp(cur.cam.cy, nxt.cam.cy, ease);
  const camW = lerp(cur.cam.w, nxt.cam.w, ease);

  // Drag-pan: applied as an offset on top of scroll-driven cam.
  const nowInFinalScene = t > FINAL_DRAG_THRESHOLD;
  if (nowInFinalScene !== dragState.enabled) {
    dragState.enabled = nowInFinalScene;
    visSection.classList.toggle("vis-draggable", nowInFinalScene);
    if (!nowInFinalScene) {
      dragState.offsetX = 0;
      dragState.offsetY = 0;
      dragState.active = false;
      visSection.classList.remove("vis-dragging");
    }
  }
  dragState.lastViewW = camW;

  const effCx = camCx + (dragState.enabled ? dragState.offsetX : 0);
  const effCy = camCy + (dragState.enabled ? dragState.offsetY : 0);

  const scale = Math.min(canvasW, canvasH) / camW;
  const tx = canvasW / 2 - effCx * scale;
  const ty = canvasH / 2 - effCy * scale;

  const autoShare = automatedShare(t);
  const layerA = layerAlphas(t);
  const clusterA = clusterAlphas(t);
  const tSec = now / 1000;

  // Clear in identity space, then enter world space.
  ctx.setTransform(1, 0, 0, 1, 0, 0);
  ctx.clearRect(0, 0, canvas.width, canvas.height);
  ctx.setTransform(dpr * scale, 0, 0, dpr * scale, dpr * tx, dpr * ty);

  // ---- Cluster rings (back layer) ----
  ctx.setLineDash([3, 6]);
  ctx.strokeStyle = "rgba(180,200,240,0.22)";
  ctx.lineWidth = 0.8;
  for (let layer = 0; layer < LAYER_COUNT; layer++) {
    const arr = clusterByLayer[layer];
    if (!arr || arr.length === 0) continue;
    const la = clusterA[layer];
    if (la <= 0.001) continue;
    // Big-org rings first
    let bigAny = false, smallAny = false;
    ctx.globalAlpha = la;
    ctx.beginPath();
    for (let i = 0; i < arr.length; i++) {
      const c = arr[i];
      if (c.small) { smallAny = true; continue; }
      ctx.moveTo(c.x + c.r, c.y);
      ctx.arc(c.x, c.y, c.r, 0, Math.PI * 2);
      bigAny = true;
    }
    if (bigAny) ctx.stroke();
    if (smallAny) {
      ctx.setLineDash([2, 4]);
      ctx.strokeStyle = "rgba(180,200,240,0.16)";
      ctx.lineWidth = 0.5;
      ctx.beginPath();
      for (let i = 0; i < arr.length; i++) {
        const c = arr[i];
        if (!c.small) continue;
        ctx.moveTo(c.x + c.r, c.y);
        ctx.arc(c.x, c.y, c.r, 0, Math.PI * 2);
      }
      ctx.stroke();
      ctx.setLineDash([3, 6]);
      ctx.strokeStyle = "rgba(180,200,240,0.22)";
      ctx.lineWidth = 0.8;
    }
  }
  ctx.setLineDash([]);

  // Cluster labels (only ~10 of them — fillText per label is fine).
  ctx.textAlign = "center";
  ctx.textBaseline = "alphabetic";
  ctx.lineJoin = "round";
  for (let layer = 0; layer < LAYER_COUNT; layer++) {
    const arr = clusterByLayer[layer];
    if (!arr || arr.length === 0) continue;
    const la = clusterA[layer];
    if (la <= 0.01) continue;
    ctx.globalAlpha = la;
    for (let i = 0; i < arr.length; i++) {
      const c = arr[i];
      if (!c.label) continue;
      if (c.small) {
        ctx.font = "600 7px -apple-system, BlinkMacSystemFont, Inter, sans-serif";
        ctx.lineWidth = 2;
        ctx.fillStyle = "rgba(231,236,246,0.45)";
      } else {
        ctx.font = "600 11px -apple-system, BlinkMacSystemFont, Inter, sans-serif";
        ctx.lineWidth = 3;
        ctx.fillStyle = "rgba(231,236,246,0.7)";
      }
      ctx.strokeStyle = "rgba(5,10,22,0.85)";
      const ly = c.y - c.r - 8;
      ctx.strokeText(c.label, c.x, ly);
      ctx.fillText(c.label, c.x, ly);
    }
  }

  // ---- Edges (batched by style + layer) ----
  // 1) base — always gray
  ctx.strokeStyle = "rgba(160,160,200,0.18)";
  ctx.lineWidth = 0.8;
  for (let layer = 0; layer < LAYER_COUNT; layer++) {
    strokeEdgeBatch(edgeBuckets.base[layer], layerA[layer]);
  }
  // 2) canAutomate — gray below threshold, teal above
  if (autoShare > 0.4) {
    ctx.strokeStyle = "rgba(109,214,255,0.6)";
    ctx.lineWidth = 1.1;
  } else {
    ctx.strokeStyle = "rgba(160,160,200,0.18)";
    ctx.lineWidth = 0.8;
  }
  for (let layer = 0; layer < LAYER_COUNT; layer++) {
    strokeEdgeBatch(edgeBuckets.canAutomate[layer], layerA[layer]);
  }
  // 3) automated (non-pulse)
  ctx.strokeStyle = "rgba(109,214,255,0.55)";
  ctx.lineWidth = 1;
  for (let layer = 0; layer < LAYER_COUNT; layer++) {
    strokeEdgeBatch(edgeBuckets.automated[layer], layerA[layer]);
  }
  // 4) pulse filaments (dashed + animated flow)
  ctx.strokeStyle = "rgba(109,214,255,0.75)";
  ctx.lineWidth = 1.4;
  ctx.setLineDash([4, 16]);
  ctx.lineDashOffset = -((tSec * 80) % 200);
  for (let layer = 0; layer < LAYER_COUNT; layer++) {
    strokeEdgeBatch(edgeBuckets.pulse[layer], layerA[layer]);
  }
  ctx.setLineDash([]);
  ctx.lineDashOffset = 0;

  // ---- Node halos (sprite groups) ----
  // Pulse periods are per-color category — all members of a sprite group share
  // the same pulse value at a given time.
  const pulseHuman    = 0.85 + 0.15 * Math.sin(tSec * (Math.PI * 2 / 4.0));
  const pulseTrigger  = 0.85 + 0.15 * Math.sin(tSec * (Math.PI * 2 / 1.8));
  const pulseAutoCore = 0.85 + 0.15 * Math.sin(tSec * (Math.PI * 2 / 2.2));
  const coreFlipped = autoShare > 0.5;

  drawHaloGroup(nodeBuckets.midHumans,   sprites.normal.amber,   pulseHuman,   layerA);
  drawHaloGroup(nodeBuckets.farHumans,   sprites.far.amber,      pulseHuman,   layerA);
  drawHaloGroup(nodeBuckets.midTriggers, sprites.normal.magenta, pulseTrigger, layerA);
  drawHaloGroup(nodeBuckets.farTriggers, sprites.far.magenta,    pulseTrigger, layerA);
  // coreLinear shares one sprite at a time (flips with autoShare).
  {
    const arr = nodeBuckets.coreLinear;
    if (arr.length > 0 && layerA[0] > 0.001) {
      const sprite = coreFlipped ? sprites.core.teal : sprites.core.amber;
      const pulse = coreFlipped ? pulseAutoCore : pulseHuman;
      ctx.globalAlpha = layerA[0] * pulse;
      for (let i = 0; i < arr.length; i++) {
        const n = arr[i];
        ctx.drawImage(sprite.canvas, n.x - sprite.r, n.y - sprite.r);
      }
    }
  }

  // ---- Node cores (batched arcs by fill color) ----
  ctx.strokeStyle = "rgba(255,255,255,0.4)";
  ctx.lineWidth = 0.4;
  drawCoreGroup(nodeBuckets.midHumans,   "#f5a056", 3.6, layerA);
  drawCoreGroup(nodeBuckets.farHumans,   "#f5a056", 2.2, layerA);
  drawCoreGroup(nodeBuckets.midTriggers, "#ff5d8f", 3.6, layerA);
  drawCoreGroup(nodeBuckets.farTriggers, "#ff5d8f", 2.2, layerA);
  // coreLinear cores — heavier stroke, larger radius.
  {
    const arr = nodeBuckets.coreLinear;
    if (arr.length > 0 && layerA[0] > 0.001) {
      ctx.lineWidth = 0.6;
      ctx.fillStyle = coreFlipped ? "#6dd6ff" : "#f5a056";
      ctx.globalAlpha = layerA[0];
      ctx.beginPath();
      for (let i = 0; i < arr.length; i++) {
        const n = arr[i];
        ctx.moveTo(n.x + 5, n.y);
        ctx.arc(n.x, n.y, 5, 0, Math.PI * 2);
      }
      ctx.fill();
      ctx.stroke();
      // Labels — 5 of them, fine to do per-label.
      ctx.font = "600 9px -apple-system, BlinkMacSystemFont, Inter, sans-serif";
      ctx.textAlign = "left";
      ctx.textBaseline = "alphabetic";
      ctx.lineWidth = 2.5;
      ctx.lineJoin = "round";
      ctx.strokeStyle = "rgba(5,10,22,0.8)";
      ctx.fillStyle = "rgba(231,236,246,0.85)";
      for (let i = 0; i < arr.length; i++) {
        const n = arr[i];
        if (!n.label) continue;
        ctx.strokeText(n.label, n.x + 12, n.y + 4);
        ctx.fillText(n.label, n.x + 12, n.y + 4);
      }
    }
  }

  ctx.globalAlpha = 1;

  // ---- Overlay DOM ----
  // Card crossfade: toggle .vis-card-fading for 180ms, swap text, fade back in.
  const dom = frac < 0.55 ? idx : Math.min(SCENES.length - 1, idx + 1);
  if (titleEl && titleEl.dataset.idx !== String(dom)) {
    titleEl.dataset.idx = String(dom);
    if (cardEl) {
      cardEl.classList.add("vis-card-fading");
      cardEl.classList.add("vis-card-flash");
    }
    if (pendingTextSwap) clearTimeout(pendingTextSwap);
    const targetIdx = dom;
    pendingTextSwap = setTimeout(() => {
      if (titleEl) titleEl.textContent = SCENES[targetIdx].title;
      if (bodyEl)  bodyEl.textContent  = SCENES[targetIdx].body;
      if (cardEl)  cardEl.classList.remove("vis-card-fading");
      pendingTextSwap = null;
    }, 180);
    if (cardEl) setTimeout(() => cardEl.classList.remove("vis-card-flash"), 360);
  }
  if (finalEl) finalEl.classList.toggle("visible", t > 6.7);
  if (progressFillEl) progressFillEl.style.width = `${(progress * 100).toFixed(2)}%`;
}

function drawHaloGroup(layerArr, sprite, pulse, layerA) {
  for (let layer = 0; layer < LAYER_COUNT; layer++) {
    const ns = layerArr[layer];
    if (!ns || ns.length === 0) continue;
    const la = layerA[layer];
    if (la <= 0.001) continue;
    ctx.globalAlpha = la * pulse;
    for (let i = 0; i < ns.length; i++) {
      const n = ns[i];
      ctx.drawImage(sprite.canvas, n.x - sprite.r, n.y - sprite.r);
    }
  }
}

function drawCoreGroup(layerArr, fillColor, coreR, layerA) {
  ctx.fillStyle = fillColor;
  for (let layer = 0; layer < LAYER_COUNT; layer++) {
    const ns = layerArr[layer];
    if (!ns || ns.length === 0) continue;
    const la = layerA[layer];
    if (la <= 0.001) continue;
    ctx.globalAlpha = la;
    ctx.beginPath();
    for (let i = 0; i < ns.length; i++) {
      const n = ns[i];
      ctx.moveTo(n.x + coreR, n.y);
      ctx.arc(n.x, n.y, coreR, 0, Math.PI * 2);
    }
    ctx.fill();
    ctx.stroke();
  }
}

// ---------- rAF loop ----------

function frame(now) {
  if (!runRaf) return;
  if (!visSection || !visSection.classList.contains("active")) {
    runRaf = false;
    return;
  }
  draw(now);
  requestAnimationFrame(frame);
}

function startLoop() {
  if (runRaf) return;
  runRaf = true;
  requestAnimationFrame(frame);
}

function onScroll() { startLoop(); }

// ---------- Drag-pan in the final scene ----------

function pixelToWorld(deltaPx, renderedEdge, viewW) {
  if (!renderedEdge) return 0;
  return deltaPx * (viewW / renderedEdge);
}

function onDragPointerDown(e) {
  if (!dragState.enabled) return;
  if (e.button !== undefined && e.button !== 0) return;
  if (!canvas) return;
  dragState.active = true;
  dragState.pointerId = e.pointerId;
  dragState.startX = e.clientX;
  dragState.startY = e.clientY;
  dragState.startOffX = dragState.offsetX;
  dragState.startOffY = dragState.offsetY;
  if (visSection) visSection.classList.add("vis-dragging");
  try { canvas.setPointerCapture(e.pointerId); } catch {}
  e.preventDefault();
}

function onDragPointerMove(e) {
  if (!dragState.active || e.pointerId !== dragState.pointerId) return;
  const dxPx = e.clientX - dragState.startX;
  const dyPx = e.clientY - dragState.startY;
  const renderedEdge = Math.min(canvasW, canvasH);
  // Dragging right reveals content to the left → camera shifts left → negate.
  const dxWorld = -pixelToWorld(dxPx, renderedEdge, dragState.lastViewW);
  const dyWorld = -pixelToWorld(dyPx, renderedEdge, dragState.lastViewW);
  dragState.offsetX = Math.max(-DRAG_CLAMP, Math.min(DRAG_CLAMP, dragState.startOffX + dxWorld));
  dragState.offsetY = Math.max(-DRAG_CLAMP, Math.min(DRAG_CLAMP, dragState.startOffY + dyWorld));
  startLoop();
  e.preventDefault();
}

function onDragPointerEnd() {
  if (!dragState.active) return;
  if (canvas && dragState.pointerId != null) {
    try { canvas.releasePointerCapture(dragState.pointerId); } catch {}
  }
  dragState.active = false;
  dragState.pointerId = null;
  if (visSection) visSection.classList.remove("vis-dragging");
}

// ---------- Lifecycle ----------
//
// Phase 7 of the React-engine port lifts the old `activateVision` /
// `deactivateVision` trampolines into `Vision.tsx`. The React
// component owns the `<section id="vision">` and `<canvas
// id="vis-canvas">` (plus the title/body/card/final/progress overlay
// nodes) via refs, calls `initVision({...refs})` once in a
// `useEffect`, and calls `teardownVision()` from cleanup. Body-class
// state (`vis-mode`) is owned by `PilotProvider`.
//
// All event listeners (window scroll/resize, canvas pointer events)
// are attached here and torn down in `teardownVision` so a navigate-
// away leaves no listeners attached to the document.

let cachedRafId = 0;
let onScrollListener = null;
let onResizeListener = null;
let pointerDownListener = null;
let pointerMoveListener = null;
let pointerUpListener = null;
let pointerCancelListener = null;
let pointerLeaveListener = null;

export function initVision(refs) {
  // refs: { canvas, section, titleEl, bodyEl, cardEl, finalEl, progressFillEl }
  // All are required (React provides them from useRef once the
  // component has mounted into the DOM).
  if (initialized) {
    // Defensive: a previous teardown should always run before re-init,
    // so this is an error state — bail to avoid double-wiring listeners.
    return;
  }
  canvas = refs.canvas;
  if (!canvas) return;
  ctx = canvas.getContext("2d");
  cachedGraph = buildGraph();
  sprites = buildSprites();
  const buckets = buildBuckets(cachedGraph);
  edgeBuckets = buckets.edgeBuckets;
  nodeBuckets = buckets.nodeBuckets;
  clusterByLayer = buckets.clusterByLayer;
  visSection = refs.section;
  titleEl = refs.titleEl;
  bodyEl = refs.bodyEl;
  cardEl = refs.cardEl;
  finalEl = refs.finalEl;
  progressFillEl = refs.progressFillEl;

  // The draw() / frame() loop gates on
  // `visSection.classList.contains("active")` — preserve that contract
  // by adding the class once on init. React owns the section element;
  // this is a local class toggle, sibling to `vis-draggable` /
  // `vis-dragging` which the drag handlers toggle imperatively for
  // canvas-pointer visual state.
  if (visSection) visSection.classList.add("active");

  onScrollListener = onScroll;
  onResizeListener = onResize;
  pointerDownListener = onDragPointerDown;
  pointerMoveListener = onDragPointerMove;
  pointerUpListener = onDragPointerEnd;
  pointerCancelListener = onDragPointerEnd;
  pointerLeaveListener = onDragPointerEnd;

  window.addEventListener("scroll", onScrollListener, { passive: true });
  window.addEventListener("resize", onResizeListener, { passive: true });
  canvas.addEventListener("pointerdown", pointerDownListener);
  canvas.addEventListener("pointermove", pointerMoveListener);
  canvas.addEventListener("pointerup", pointerUpListener);
  canvas.addEventListener("pointercancel", pointerCancelListener);
  canvas.addEventListener("pointerleave", pointerLeaveListener);

  initialized = true;

  ensureCanvasSize();
  // Layout may still be settling — resize again on the next frame.
  cachedRafId = requestAnimationFrame(() => { ensureCanvasSize(); startLoop(); });

  dragState.offsetX = 0;
  dragState.offsetY = 0;
  dragState.active = false;
  dragState.enabled = false;

  window.scrollTo({ top: 0, behavior: "instant" });
  startLoop();
}

export function teardownVision() {
  // Stop the rAF loop before anything else so an in-flight frame can't
  // re-enter against a half-detached state.
  runRaf = false;
  if (cachedRafId) {
    cancelAnimationFrame(cachedRafId);
    cachedRafId = 0;
  }

  if (onScrollListener) {
    window.removeEventListener("scroll", onScrollListener);
    onScrollListener = null;
  }
  if (onResizeListener) {
    window.removeEventListener("resize", onResizeListener);
    onResizeListener = null;
  }
  if (canvas) {
    if (pointerDownListener) canvas.removeEventListener("pointerdown", pointerDownListener);
    if (pointerMoveListener) canvas.removeEventListener("pointermove", pointerMoveListener);
    if (pointerUpListener)   canvas.removeEventListener("pointerup",   pointerUpListener);
    if (pointerCancelListener) canvas.removeEventListener("pointercancel", pointerCancelListener);
    if (pointerLeaveListener)  canvas.removeEventListener("pointerleave",  pointerLeaveListener);
  }
  pointerDownListener = pointerMoveListener = pointerUpListener = null;
  pointerCancelListener = pointerLeaveListener = null;

  // Drop the imperative class flags on the section (it's about to
  // unmount, but be defensive — React might keep the node alive in
  // a transition).
  if (visSection) {
    visSection.classList.remove("active");
    visSection.classList.remove("vis-draggable");
    visSection.classList.remove("vis-dragging");
  }

  dragState.active = false;
  dragState.enabled = false;

  // Clear the cached refs so a stale frame can't draw against a
  // detached canvas.
  canvas = null;
  ctx = null;
  visSection = null;
  titleEl = null;
  bodyEl = null;
  cardEl = null;
  finalEl = null;
  progressFillEl = null;
  cachedGraph = null;
  sprites = null;
  edgeBuckets = null;
  nodeBuckets = null;
  clusterByLayer = null;
  initialized = false;
}
